import React, { Children, useEffect, useMemo, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { Node } from '@atlaskit/editor-prosemirror/model';
import { TableSharedCssClassName } from '@atlaskit/editor-common/styles';
import { hasMergedCell } from '@atlaskit/editor-common/utils/table';
import { akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles/consts';

import type { LightNodeProps } from '../../LightNodeObserver.ts';

import { registerLightTable } from './lightTableTriggers.ts';
import { getTableContainerStyle } from './tableContainerStyle.ts';
import type { TableContainerStyleProps } from './tableContainerStyle.ts';
import type { LightTableControls } from './lightTableTriggers.ts';
import { LightTableContext } from './TableCell.tsx';

type TableProps = LightNodeProps &
	Omit<TableContainerStyleProps, 'tableNode'> & {
		allowColumnSorting?: boolean;
		children?: ReactNode;
		columnWidths?: number[];
		subtreeControls?: LightTableControls;
		tableNode?: Node;
	};

// Platform makes a header row sortable only when it is all header cells and no cell is merged.
const hasSortableHeaderRow = (tableNode: Node | undefined, allowColumnSorting?: boolean) => {
	const firstRow = tableNode?.firstChild;
	if (!allowColumnSorting || !tableNode || !firstRow?.childCount) {
		return false;
	}
	let isHeaderRow = true;
	firstRow.forEach((cell) => {
		isHeaderRow &&= cell.type.name === 'tableHeader';
	});
	return isHeaderRow && !hasMergedCell(tableNode);
};

// Platform's class names apply the renderer's table borders and spacing to the light DOM.
export const Table = ({
	allowColumnSorting,
	allowTableAlignment,
	allowTableResizing,
	children,
	columnWidths,
	isInsideOfBlockNode,
	isInsideOfTable,
	lightNodeRef,
	rendererAppearance,
	subtreeControls: controls,
	tableNode,
}: TableProps): ReactElement => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const element = wrapperRef.current;
		if (!controls || !element) {
			return undefined;
		}
		// Links, mentions and inline comments need the full table before a click or selection lands.
		element.addEventListener('pointerover', controls.activateNow);
		const unregister = registerLightTable(controls);
		return () => {
			element.removeEventListener('pointerover', controls.activateNow);
			unregister();
		};
	}, [controls]);
	const isNumberColumnEnabled = Boolean(tableNode?.attrs.isNumberColumnEnabled);
	const numberColumnWidth = isNumberColumnEnabled ? akEditorTableNumberColumnWidth : 0;
	const columnsWidth = columnWidths?.reduce((sum, width) => sum + width, 0) ?? 0;
	// It evaluates an experiment with exposure, so re-renders must not evaluate it again.
	const containerStyle = useMemo(
		() =>
			getTableContainerStyle({
				allowTableAlignment,
				allowTableResizing,
				isInsideOfBlockNode,
				isInsideOfTable,
				rendererAppearance,
				tableNode,
			}),
		[
			allowTableAlignment,
			allowTableResizing,
			isInsideOfBlockNode,
			isInsideOfTable,
			rendererAppearance,
			tableNode,
		],
	);
	const isSortable = useMemo(
		() => hasSortableHeaderRow(tableNode, allowColumnSorting),
		[tableNode, allowColumnSorting],
	);
	const headerRowContext = useMemo(
		() => ({ isNumberColumnEnabled, isSortableHeaderRow: isSortable }),
		[isNumberColumnEnabled, isSortable],
	);
	const rowContext = useMemo(
		() => ({ isNumberColumnEnabled, isSortableHeaderRow: false }),
		[isNumberColumnEnabled],
	);
	const [firstRow, ...otherRows] = Children.toArray(children);
	return (
		<div
			ref={lightNodeRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's table container styles.
			className={TableSharedCssClassName.TABLE_CONTAINER}
			data-layout={tableNode?.attrs.layout}
			// Marks the table as light until its subtree upgrades to Platform's table.
			data-light-table
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Platform computes the width and breakout offset at runtime.
			style={containerStyle}
		>
			<div
				ref={wrapperRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Reuse Platform's table wrapper styles.
				className={TableSharedCssClassName.TABLE_NODE_WRAPPER}
				data-layout={tableNode?.attrs.layout}
				data-number-column={isNumberColumnEnabled || undefined}
				data-table-local-id={tableNode?.attrs.localId}
				data-table-width={tableNode?.attrs.width}
			>
				<table
					data-layout={tableNode?.attrs.layout}
					data-number-column={isNumberColumnEnabled || undefined}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Sized columns keep their ADF proportions; unsized columns share the width.
					style={{ tableLayout: 'fixed', width: '100%' }}
				>
					{(isNumberColumnEnabled || columnsWidth > 0) && (
						<colgroup>
							{isNumberColumnEnabled && (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Match Platform's number column width.
								<col style={{ width: akEditorTableNumberColumnWidth }} />
							)}
							{columnsWidth > 0 &&
								columnWidths?.map((width, index) => (
									<col
										// eslint-disable-next-line react/no-array-index-key -- Columns are positional.
										key={index}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Widths come from ADF.
										style={
											width
												? { width: `${(width / (columnsWidth + numberColumnWidth)) * 100}%` }
												: undefined
										}
									/>
								))}
						</colgroup>
					)}
					<tbody>
						<LightTableContext.Provider value={headerRowContext}>
							{firstRow}
						</LightTableContext.Provider>
						<LightTableContext.Provider value={rowContext}>{otherRows}</LightTableContext.Provider>
					</tbody>
				</table>
			</div>
		</div>
	);
};
